// TP API calls
import axios from 'axios';
import { 
  TP_SOLVE_API,
  DELETE_PATH_API,
  TP_DISTANCE_MATRIX_API,
  TP_SOLVE_TRANSPORT_API
} from '../../../../config';

// Solve TP with given costs/supply/demand
export const solveTp = async (costs, supply, demand, method = "vam", forbidden = null, log = false) => {
  try {
    const requestData = {
      costs: costs,
      supply: supply,
      demand: demand,
      method: method,
      log: log
    };
    
    if (forbidden && forbidden.length > 0) {
      requestData.forbidden = forbidden;
    }

    const response = await axios.post(TP_SOLVE_API, requestData);

    if (response.status === 200 && response.data) {
      const data = response.data;
      return {
        method: data.method || method,
        allocations: data.allocations || [],
        total_cost: data.total_cost || 0,
        steps: data.steps || [],
        lp_optimal_cost: data.lp_optimal_cost,
        lp_optimal_allocations: data.lp_optimal_allocations,
        notes: data.notes
      };
    } else {
      throw new Error(response.data.error || 'Error solving TP');
    }
  } catch (error) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(`Unexpected error solving TP: ${error.message}`);
  }
};

// Get available TP methods
export const getTpMethods = async () => {
  try {
    // TP methods are typically hardcoded as they are well-defined
    const methods = [
      { 
        name: 'vam', 
        display_name: 'Vogel\'s Approximation Method', 
        description: 'VAM heuristic method for better initial solution' 
      },
      { 
        name: 'nwc', 
        display_name: 'North-West Corner', 
        description: 'Simple allocation method starting from top-left' 
      },
      { 
        name: 'minimum_cost', 
        display_name: 'Minimum Cost Method', 
        description: 'Greedy minimum cost allocation method' 
      }
    ];
    return methods;
  } catch (error) {
    console.error('Error getting TP methods:', error);
    throw new Error(`Failed to get TP method information: ${error.message}`);
  }
};

// Delete path by ID
export const deletePath = async (pathId) => {
  try {
    const response = await axios.delete(DELETE_PATH_API(pathId));
    
    if (response.data.success) {
      return true;
    } else {
      throw new Error(response.data.error || 'Error deleting path');
    }
  } catch (error) {
    throw error;
  }
};

// Calculate distance matrix
export const calculateDistanceMatrix = async (locations) => {
  try {
    const requestData = {
      locations: locations
    };

    const response = await axios.post(TP_DISTANCE_MATRIX_API, requestData);

    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      throw new Error(response.data.error || 'Error calculating distance matrix');
    }
  } catch (error) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(`Unexpected error calculating distance matrix: ${error.message}`);
  }
};

// Solve transport problem with locations
export const solveTransport = async (
  supplyLocations, 
  demandLocations, 
  supply, 
  demand, 
  method = "vam", 
  forbidden = null, 
  log = false
) => {
  const requestData = {
    supply_locations: supplyLocations,
    demand_locations: demandLocations,
    supply: supply,
    demand: demand,
    method: method,
    log: log
  };
  
  if (forbidden && forbidden.length > 0) {
    requestData.forbidden = forbidden;
  }

  try {
    const response = await axios.post(TP_SOLVE_TRANSPORT_API, requestData);

    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      throw new Error(response.data.error || 'Error solving transport problem');
    }
  } catch (error) {
    console.error('Transport API Error:', error);
    
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      console.error('Request data that caused error:', requestData);
      
      if (error.response.data && error.response.data.detail) {
        // Handle validation errors with more detail
        if (Array.isArray(error.response.data.detail)) {
          const validationErrors = error.response.data.detail.map(err => 
            `${err.loc?.join('.')} : ${err.msg}`
          ).join('; ');
          throw new Error(`Validation failed: ${validationErrors}`);
        } else {
          throw new Error(error.response.data.detail);
        }
      } else if (error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      } else {
        throw new Error(`Server error ${error.response.status}: ${error.response.statusText}`);
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
      throw new Error('No response from server. Please check if the backend is running.');
    } else {
      console.error('Request setup error:', error.message);
      throw new Error(`Request error: ${error.message}`);
    }
  }
};
